import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { Panel } from "../components/Panel";
import { fetchContributorWorkspace, type ContributorWorkspaceOverview } from "../lib/api/runtimeWorkspaceClient";
import { contributorDepartmentPath } from "../lib/contributorRoutes";
import { statusLabel } from "../lib/departmentWorkflow";
import { useWorkspaceLanguage } from "../lib/i18n";
import { canWeightClusters, valueRoundCanAcceptInput } from "../lib/valueWorkflow";
import type { DepartmentStatus } from "../types";

export function Contributor() {
  const language = useWorkspaceLanguage();
  const copy = language === "en" ? {
    eyebrow: "My Department",
    title: "Your department in the Customer Value Workspace",
    description: "Open your personal invitation link to submit your department perspective.",
    trustTitle: "Trust note",
    trustBody: "The workspace does not evaluate people. It makes department perspectives visible.",
    loadingTitle: "Departments are loading",
    loadingBody: "Workspace data is loading...",
    loadError: "Contributor data could not be loaded.",
    loadErrorTitle: "Departments could not be loaded",
    emptyTitle: "No department assigned",
    emptyBody: "No department has been assigned to you yet.",
    myDepartment: "My Department",
    workspace: "Workspace",
    department: "Department",
    status: "Status",
    initialPerspective: "Answer perspective",
    valueRound: "Answer Value Round",
    weighting: "Weight criteria",
    done: "Done",
    notAvailable: "Not available yet",
    openNow: "Open now"
  } : {
    eyebrow: "Meine Abteilung",
    title: "Ihre Abteilung im Customer Value Workspace",
    description: "Öffnen Sie Ihren persönlichen Einladungslink, um Ihre Abteilungsperspektive einzureichen.",
    trustTitle: "Vertrauenshinweis",
    trustBody: "Der Workspace bewertet keine Personen. Er macht Abteilungsperspektiven sichtbar.",
    loadingTitle: "Abteilungen werden geladen",
    loadingBody: "D1-Daten werden geladen...",
    loadError: "Contributor-Daten konnten nicht geladen werden.",
    loadErrorTitle: "Abteilungen konnten nicht geladen werden",
    emptyTitle: "Keine Abteilung zugewiesen",
    emptyBody: "Ihnen wurde noch keine Abteilung zugewiesen.",
    myDepartment: "Meine Abteilung",
    workspace: "Workspace",
    department: "Abteilung",
    status: "Status",
    initialPerspective: "Perspektive beantworten",
    valueRound: "Value-Round beantworten",
    weighting: "Kriterien gewichten",
    done: "Erledigt",
    notAvailable: "Noch nicht verfügbar",
    openNow: "Jetzt öffnen"
  };
  const [overview, setOverview] = useState<ContributorWorkspaceOverview>({ departments: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let isMounted = true;

    async function loadContributorWorkspace() {
      setIsLoading(true);
      setError(undefined);
      try {
        const nextOverview = await fetchContributorWorkspace();
        if (isMounted) setOverview(nextOverview);
      } catch (caught) {
        if (isMounted) setError(caught instanceof Error ? caught.message : copy.loadError);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    void loadContributorWorkspace();

    return () => {
      isMounted = false;
    };
  }, [copy.loadError]);

  return (
    <Layout
      eyebrow={copy.eyebrow}
      title={copy.title}
      description={copy.description}
    >
      <Panel title={copy.trustTitle} variant="accent">
        <p>{copy.trustBody}</p>
      </Panel>

      {isLoading ? <Panel title={copy.loadingTitle}><p>{copy.loadingBody}</p></Panel> : null}
      {error ? (
        <Panel title={copy.loadErrorTitle} variant="accent">
          <p className="form-error" role="alert">{error}</p>
        </Panel>
      ) : null}
      {!isLoading && !error && overview.departments.length === 0 ? (
        <Panel title={copy.emptyTitle} variant="accent">
          <p>{copy.emptyBody}</p>
        </Panel>
      ) : null}

      {overview.departments.map((department) => {
        const departmentPath = contributorDepartmentPath(department.id);
        const initialDone = department.responseStatus === "submitted" || department.status === "submitted" || department.status === "reviewed";
        const valueRoundDone = department.valueInputStatus === "submitted";
        const valueRoundAvailable = valueRoundCanAcceptInput(overview.process?.status);
        const weightingDone = department.weightingStatus === "submitted";
        const weightingAvailable = canWeightClusters(overview.process?.status);
        const tasks = [
          {
            title: copy.initialPerspective,
            status: initialDone ? "done" : "available",
            statusLabel: initialDone ? copy.done : copy.openNow,
            href: departmentPath
          },
          {
            title: copy.valueRound,
            status: valueRoundDone ? "done" : valueRoundAvailable ? "available" : "locked",
            statusLabel: valueRoundDone ? copy.done : valueRoundAvailable ? copy.openNow : copy.notAvailable,
            href: valueRoundAvailable || valueRoundDone ? `${departmentPath}#value-round` : undefined
          },
          {
            title: copy.weighting,
            status: weightingDone ? "done" : weightingAvailable ? "available" : "locked",
            statusLabel: weightingDone ? copy.done : weightingAvailable ? copy.openNow : copy.notAvailable,
            href: weightingAvailable || weightingDone ? `${departmentPath}/weighting` : undefined
          }
        ];

        return (
          <Panel key={department.id} title={copy.myDepartment}>
            <div className="data-list">
              <div>
                <span>{copy.workspace}</span>
                <strong>{department.workspaceName}</strong>
              </div>
              <div>
                <span>{copy.department}</span>
                <strong>{department.name}</strong>
              </div>
              <div>
                <span>{copy.status}</span>
                <strong className={`status-badge status-${department.status}`}>
                  {statusLabel(department.status as DepartmentStatus, language)}
                </strong>
              </div>
            </div>
            <div className="contributor-task-grid" aria-label={copy.myDepartment}>
              {tasks.map((task) => (
                <article key={task.title} className={`contributor-task-card contributor-task-card--${task.status}`}>
                  <div>
                    <strong>{task.title}</strong>
                    <span className={`status-badge contributor-task-status contributor-task-status--${task.status}`}>
                      {task.statusLabel}
                    </span>
                  </div>
                  {task.href ? (
                    <a className="button button-secondary" href={task.href}>
                      {copy.openNow}
                    </a>
                  ) : (
                    <span className="button button-secondary button-disabled" aria-disabled="true">
                      {copy.notAvailable}
                    </span>
                  )}
                </article>
              ))}
            </div>
          </Panel>
        );
      })}
    </Layout>
  );
}
